import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type React from "react";
import { ProductShot } from "@/components/blocks/ProductShot";
import { ChouicHeroScene } from "@/components/blocks/scenes/ChouicScenes";
import { EditorScene } from "@/components/blocks/scenes/QuestoveryScreens";
import { QuestPhone } from "@/components/blocks/scenes/QuestScene";
import { VaerdictSimulationScene } from "@/components/blocks/scenes/VaerdictScreens";
import { Cta } from "@/components/blocks/sections/Cta";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { Section } from "@/components/blocks/sections/Section";
import { Overline } from "@/components/blocks/sections/SectionHeading";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/motion";
import { Button } from "@/components/ui/button";
import type { Translations } from "@/lib/i18n";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const CTA_ID = "contact";

type CaseKey = keyof Translations["fr-FR"]["work"]["items"];

/** Cases in display order: client projects first, then Lezo's own products. */
const CASES: {
	key: CaseKey;
	to:
		| "/{-$locale}/realisations/vaerdict"
		| "/{-$locale}/realisations/chouic"
		| "/{-$locale}/realisations/questovery";
	visual: React.ReactNode;
}[] = [
	{
		key: "vaerdict",
		to: "/{-$locale}/realisations/vaerdict",
		visual: <VaerdictSimulationScene />,
	},
	{
		key: "chouic",
		to: "/{-$locale}/realisations/chouic",
		visual: <ChouicHeroScene />,
	},
	{
		key: "questovery",
		to: "/{-$locale}/realisations/questovery",
		visual: (
			<div className="relative pr-10 pb-10 sm:pr-16">
				<EditorScene />
				<div className="absolute right-0 -bottom-2 w-[38%] max-w-[11rem]">
					<QuestPhone
						index={2}
						className="rounded-[1.8rem] p-[5px] [&>div]:rounded-[1.5rem]"
					/>
				</div>
			</div>
		),
	},
];

export function WorkPage() {
	const { dict, localeParams } = useI18n();
	const page = dict.work;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
			/>

			<Section containerClassName="space-y-10">
				{CASES.map(({ key, to, visual }, caseIndex) => {
					const item = page.items[key];
					return (
						<Reveal
							key={key}
							as="section"
							className="group grid overflow-hidden rounded-[2rem] border bg-card lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]"
						>
							<div
								className={cn(
									"dot-grid relative flex items-center justify-center overflow-hidden bg-muted/70 px-6 py-12 md:px-12 md:py-16",
									caseIndex % 2 === 1 && "lg:order-last",
								)}
							>
								<ProductShot
									alt={item.visualAlt}
									className="w-full max-w-xl transition-transform duration-700 ease-(--ease-soft) group-hover:-translate-y-1"
									fallback={visual}
								/>
							</div>
							<div className="flex flex-col gap-6 p-8 md:p-12">
								<Overline>{item.badge}</Overline>
								<h2 className="text-[clamp(2rem,3.4vw,3rem)] leading-none font-semibold tracking-[-0.06em]">
									{item.title}
								</h2>
								<p className="text-lg text-pretty text-muted-foreground">
									{item.summary}
								</p>
								<dl className="grid gap-5 border-t pt-6 text-sm">
									<div>
										<dt className="font-mono text-[11px] tracking-[0.07em] text-muted-foreground uppercase">
											{page.roleLabel}
										</dt>
										<dd className="mt-1.5 text-base">{item.role}</dd>
									</div>
									<div>
										<dt className="font-mono text-[11px] tracking-[0.07em] text-muted-foreground uppercase">
											{page.scopeLabel}
										</dt>
										<dd className="mt-1.5 text-base">{item.scope}</dd>
									</div>
								</dl>
								<div className="mt-auto pt-2">
									<Button
										size="lg"
										className="group/btn h-auto min-h-11 rounded-lg px-6 font-mono text-[13px]"
										asChild
									>
										<Link to={to} params={localeParams}>
											{item.link}
											<ArrowRight className="transition-transform group-hover/btn:translate-x-1" />
										</Link>
									</Button>
								</div>
							</div>
						</Reveal>
					);
				})}

				<RevealGroup className="grid gap-10 border-t pt-12 md:grid-cols-2 md:gap-16">
					{[page.policy, page.upcoming].map((block) => (
						<RevealItem key={block.title} className="space-y-3">
							<h2 className="text-2xl font-semibold tracking-[-0.025em]">
								{block.title}
							</h2>
							<p className="text-pretty text-muted-foreground">
								{block.description}
							</p>
						</RevealItem>
					))}
				</RevealGroup>
			</Section>

			<Cta id={CTA_ID} {...page.cta} placement="work_cta" />
		</>
	);
}
